import {useQuery} from '@tanstack/react-query';
import api from './client';
import {get, post, put} from './http';
import {type ColumnMapping, type CustomerFeedbackUpload} from './submissions';

// A customer's feedback spreadsheet PRI reads itself: PRI.Scheduler fetches it daily (or on Sync
// now) and keeps the rows; nothing is written until someone reviews the preview and confirms.
export type CustomerFeedbackSource = {
  id: string;
  name: string;
  spreadsheet_id: string;
  tab: string;
  enabled: boolean;
  fetch_requested_at: string | null;
  last_fetched_at: string | null;
  last_fetch_error: string | null;
  // A fetch has rows no one has confirmed yet.
  has_pending: boolean;
  last_applied_at: string | null;
  last_applied_by: string | null;
  modified_by: string;
  modified_at: string;
};

export type CustomerFeedbackSources = {
  sources: CustomerFeedbackSource[];
  // Who each sheet must be shared with, as Viewer. Null in local dev.
  service_account_email: string | null;
};

export type CustomerFeedbackSourceRequest = {
  name: string;
  spreadsheet_id: string;
  tab: string;
  enabled: boolean;
};

// Accepts a pasted sheet URL as well as a bare id - the API only takes the id.
export function spreadsheetIdFrom(value: string): string {
  return /\/spreadsheets\/d\/([^#/?]+)/.exec(value)?.[1] ?? value.trim();
}

export const customerFeedbackSourcesApi = {
  list: async () => get<CustomerFeedbackSources>('/customer-feedback/sources'),

  create: async (body: CustomerFeedbackSourceRequest) =>
    post<CustomerFeedbackSourceRequest, CustomerFeedbackSource>('/customer-feedback/sources', body),

  update: async (id: string, body: CustomerFeedbackSourceRequest) =>
    put<CustomerFeedbackSourceRequest, CustomerFeedbackSource>(
      `/customer-feedback/sources/${id}`,
      body
    ),

  // Saved on the source, so the sheet is read this way from now on; the next pending() reflects it.
  setColumnMapping: async (id: string, mapping: ColumnMapping) =>
    put<{mapping: ColumnMapping}, CustomerFeedbackSource>(
      `/customer-feedback/sources/${id}/column-mapping`,
      {mapping}
    ),

  sync: async (id: string) =>
    post<undefined, CustomerFeedbackSource>(`/customer-feedback/sources/${id}/sync`),

  // Null when nothing is waiting (the API's 204).
  async pending(id: string) {
    const response = await api.get<CustomerFeedbackUpload | ''>(
      `/customer-feedback/sources/${id}/pending`
    );
    return response.status === 204 || !response.data ? null : response.data;
  },

  apply: async (id: string, fileSha256: string) =>
    post<{file_sha256: string}, CustomerFeedbackUpload>(`/customer-feedback/sources/${id}/apply`, {
      file_sha256: fileSha256
    })
};

export const customerFeedbackSourcesQueryKey = ['customer-feedback-sources'] as const;

// BroadView-only endpoint, so callers pass whether the user may ask. While a Sync now is waiting
// for the scheduler (it picks it up within a minute), poll so the result shows without a reload.
export function useCustomerFeedbackSources(enabled: boolean) {
  return useQuery({
    queryKey: customerFeedbackSourcesQueryKey,
    queryFn: customerFeedbackSourcesApi.list,
    enabled,
    refetchInterval: (query) =>
      query.state.data?.sources.some((s) => s.fetch_requested_at) ? 10_000 : false
  });
}
